import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {ListView} from 'shells/list-view/list-view';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {ProspectorCard} from 'shells/prospector-card/prospector-card';
import {Body} from 'shells/typography/body';

import type {
    GetProspectorContacts_prospectorContacts_mcfxContacts as GetProspectorContacts,
    ProspectorContactsAddInput,
} from 'nutshell-graphql-types';

import {AUTO_DISTRIBUTE_OPTION} from '../../../core/form/fields/consts';

import {addNotification} from '../../../../notifications/index';

import {AddProspectsPopover} from './add-prospects-popover';
import {notifyUser, containsProspectorContact, pruneMustHaveFilters} from './helpers';
import {NoFiltersEmptyState} from './no-filters-empty-state';
import {NoResultsEmptyState} from './no-results-empty-state';

type Props = {
    prospectorContacts: GetProspectorContacts[];
    isLoading: boolean;
    numFetchedContacts: number;
    filters: FilterObject[];
    onRemoveAllFilters: () => void;
    onRefetch: () => void;
    createProspectorContacts: (input: ProspectorContactsAddInput) => Promise<any>;
};

export function ProspectorMainContent(props: Props) {
    const {prospectorContacts, isLoading, numFetchedContacts, onRefetch} = props;
    const propertyBasedFilters = pruneMustHaveFilters(props.filters);

    // Are there filters that warrant us actually querying PDL?
    const isFilteringMoreThanSettings = Boolean(propertyBasedFilters.length);

    const [selectedProspectorContacts, setSelectedProspectorContacts] = React.useState<
        GetProspectorContacts[]
    >([]);
    const [isPopoverVisible, setIsPopoverVisible] = React.useState<boolean>(false);

    const buttonRef = React.useRef<HTMLElement | null | undefined>(null);

    const numSelectedProspectorContacts = selectedProspectorContacts.length;
    const userFacingTerm = numSelectedProspectorContacts === 1 ? 'person' : 'people';

    const handleDeselection = () => setSelectedProspectorContacts([]);

    const handlePopoverVisibility = () =>
        setIsPopoverVisible((prevIsPopoverVisible) => !prevIsPopoverVisible);

    const handleSelection = (selectedProspectorContact: GetProspectorContacts) => {
        const selectedId = selectedProspectorContact.id;

        setSelectedProspectorContacts((prevSelectedProspectorContacts) => {
            const isSelected = containsProspectorContact(
                prevSelectedProspectorContacts,
                selectedProspectorContact
            );

            return isSelected
                ? prevSelectedProspectorContacts.filter(
                      (prevSelectedProspectorContact) =>
                          prevSelectedProspectorContact.id !== selectedId
                  )
                : [...prevSelectedProspectorContacts, selectedProspectorContact];
        });
    };

    const handleSubmit = (shouldCreateNewLeads: boolean, stagesetId?: string | null) => {
        if (selectedProspectorContacts.length > 0) {
            const pdlIds = selectedProspectorContacts.map(
                (prospectorContact) => prospectorContact.id
            );

            props
                .createProspectorContacts({
                    pdlIds: pdlIds,
                    shouldCreateNewLeads: shouldCreateNewLeads,
                    stagesetId: stagesetId === AUTO_DISTRIBUTE_OPTION.value ? null : stagesetId,
                })
                .then((response) => {
                    setSelectedProspectorContacts([]);
                    notifyUser(response.data);
                    onRefetch();
                })
                .catch(() => {
                    setSelectedProspectorContacts([]);
                    addNotification('', {
                        body: 'An error occurred while saving. Please try again later.',
                        icon: 'frowny',
                    });
                });
        }
    };

    React.useEffect(() => {
        if (numSelectedProspectorContacts === 0 || !isFilteringMoreThanSettings) {
            setIsPopoverVisible(false);
        }
    }, [numSelectedProspectorContacts, isFilteringMoreThanSettings]);

    // We need to stringify the filters to compare them
    const filters = JSON.stringify(props.filters);

    // When filters change, we want to de-select any currently selected contacts
    // This also includes settings-based filters in the case that those are
    // changed _while_ other property-based filters are also applied.
    React.useEffect(() => {
        setSelectedProspectorContacts([]);
    }, [filters]);

    return (
        <div className='flex flex-dir-col full-height pos-relative overflow-auto'>
            <ListView
                collection={prospectorContacts}
                isLoading={isLoading}
                loadingState={{
                    customComponent: (
                        <div className='mt-64'>
                            <LoadingPage />
                        </div>
                    ),
                }}
                isUpdating={isLoading && Boolean(isFilteringMoreThanSettings)}
                shouldScrollToTopWhenUpdating={true}
                renderRow={(prospectorContact: GetProspectorContacts) => (
                    <ProspectorCard
                        contact={{
                            name: prospectorContact.fullName || '',
                            initials: prospectorContact.initials || '',
                            jobTitle: prospectorContact.jobTitle || '',
                            department: prospectorContact.industry || '',
                            location: prospectorContact.location || '',
                        }}
                        account={{
                            name: prospectorContact.companyName || '',
                            websiteUrl: prospectorContact.companyWebsite || '',
                            industry: prospectorContact.companyIndustry || '',
                            size: prospectorContact.companySize || {gte: null, lte: null},
                            location: prospectorContact.companyLocation || '',
                            revenue: prospectorContact.companyRevenue || {gte: null, lte: null},
                        }}
                        hasEmail={prospectorContact.hasEmail}
                        hasPhone={prospectorContact.hasPhone}
                        hasLinkedinUrl={prospectorContact.hasLinkedinUrl}
                        isSelected={containsProspectorContact(
                            selectedProspectorContacts,
                            prospectorContact
                        )}
                        onClick={() => handleSelection(prospectorContact)}
                    />
                )}
                itemSpacing={16}
                listPadding={16}
                specialStates={[
                    {
                        shouldRender: () =>
                            Boolean(isFilteringMoreThanSettings) &&
                            (numFetchedContacts === 0 || prospectorContacts.length === 0),
                        component: (
                            <NoResultsEmptyState onRemoveAllFilters={props.onRemoveAllFilters} />
                        ),
                    },
                    {
                        shouldRender: () => !isFilteringMoreThanSettings,
                        component: <NoFiltersEmptyState />,
                    },
                ]}
                isFullHeight={true}
                isOverflowAuto={true}
            />
            {numSelectedProspectorContacts > 0 && (
                <div className='flex align-center background-white justify-sb pad-16 border-top'>
                    <Body>{numSelectedProspectorContacts} Selected</Body>
                    <StackLegacy isHorizontal={true} spacing={16} alignCenter={true}>
                        <Button variant='text' onClick={handleDeselection}>
                            Deselect all
                        </Button>
                        <Button
                            variant='save'
                            getButtonRef={buttonRef}
                            onClick={handlePopoverVisibility}
                            size='big'
                        >
                            Add {numSelectedProspectorContacts} {userFacingTerm}
                        </Button>
                    </StackLegacy>
                </div>
            )}
            {isPopoverVisible && (
                <AddProspectsPopover
                    selectedContacts={selectedProspectorContacts}
                    anchor={buttonRef.current}
                    onBlur={() => setIsPopoverVisible(false)}
                    onClose={() => setIsPopoverVisible(false)}
                    onSubmit={handleSubmit}
                />
            )}
        </div>
    );
}
